/**
 * Discipline per session and per agent: each live session's score comes from the files its
 * checkout changed (new violations only); an agent's score is the average over its sessions,
 * with a trend built from their histories. Pure functions.
 */
import type { DisciplineScorePoint, DisciplineSessionReport, DisciplineState, Id, Project, Session } from '@orgus/protocol';
import { healthOfScore } from './model.ts';
import type { RepoHealth } from './model.ts';

export interface SessionRow {
  report: DisciplineSessionReport;
  history: DisciplineScorePoint[];
  projectName: string;
  title: string;
  health: RepoHealth;
  /** The session as the hub knows it now (null once it is gone). */
  live: Session | null;
}

export function sessionRows(state: DisciplineState | undefined, projects: readonly Project[], sessions: Record<Id, Session>): SessionRow[] {
  const names = new Map(projects.map((p) => [p.id, p.name]));
  return (state?.sessions ?? [])
    .map((report) => {
      const live = sessions[report.sessionId] ?? null;
      return {
        report,
        history: state?.sessionHistory?.[report.sessionId] ?? [],
        projectName: names.get(report.projectId) ?? report.projectId,
        title: live?.title || report.title || report.sessionId.slice(0, 8),
        health: report.error ? ('error' as const) : healthOfScore(report.score),
        live,
      };
    })
    .sort((a, b) => Number(!!b.live && b.live.status !== 'ended') - Number(!!a.live && a.live.status !== 'ended') || a.report.score - b.report.score || b.report.checkedAt - a.report.checkedAt);
}

export interface AgentRow {
  agent: string;
  sessions: number;
  launched: number;
  /** Average of each session's newest score, or null when none has one. */
  score: number | null;
  errors: number;
  warns: number;
  /** Average score per hour across the agent's sessions, oldest first. */
  trend: number[];
  health: RepoHealth;
}

const HOUR = 3_600_000;

export function agentRows(state: DisciplineState | undefined, buckets = 24): AgentRow[] {
  const by = new Map<string, DisciplineSessionReport[]>();
  for (const s of state?.sessions ?? []) by.set(s.agent, [...(by.get(s.agent) ?? []), s]);
  const rows: AgentRow[] = [];
  for (const [agent, list] of by) {
    const scored = list.filter((s) => !s.error);
    const score = scored.length ? Math.round(scored.reduce((n, s) => n + s.score, 0) / scored.length) : null;
    const hours = new Map<number, number[]>();
    for (const s of scored) {
      for (const p of state?.sessionHistory?.[s.sessionId] ?? [{ at: s.checkedAt, score: s.score, errors: s.errors, warns: s.warns }]) {
        const h = Math.floor(p.at / HOUR);
        hours.set(h, [...(hours.get(h) ?? []), p.score]);
      }
    }
    const trend = [...hours.entries()]
      .sort((a, b) => a[0] - b[0])
      .slice(-buckets)
      .map(([, xs]) => Math.round(xs.reduce((a, b) => a + b, 0) / xs.length));
    rows.push({
      agent,
      sessions: list.length,
      launched: list.filter((s) => s.launched).length,
      score,
      errors: scored.reduce((n, s) => n + s.errors, 0),
      warns: scored.reduce((n, s) => n + s.warns, 0),
      trend,
      health: score === null ? 'pending' : healthOfScore(score),
    });
  }
  return rows.sort((a, b) => (a.score ?? 101) - (b.score ?? 101) || b.sessions - a.sessions || a.agent.localeCompare(b.agent));
}

/** Live sessions Orgus launched in a project: the ones a violation can be sent back to. */
export function sendBackTargets(sessions: Record<Id, Session>, projectId: Id): Session[] {
  return Object.values(sessions)
    .filter((s) => s.projectId === projectId && s.origin === 'orgus' && s.status !== 'ended')
    .sort((a, b) => b.lastActivity - a.lastActivity);
}
